import jsPDF from 'jspdf';
import { replaceVariables } from './proposalDefaultData';
import { buildQuoteIntoDoc, computeQuoteFinancials } from './pdfExport';
import {
  NAVY, GOLD, LIGHT_GRAY, TEXT_COLOR, MID_GRAY, WHITE, PAGE, CONTENT_WIDTH,
  loadImageAsBase64, ensureSpace, addWrappedText, addFooters, savePDF,
  hexToRgb, formatDate,
} from './pdfShared';
import { getBRAND_NAME } from './brandConfig';

// ── Reused utility functions: all imported from pdfShared.js ───────────────
// loadImageAsBase64, ensureSpace, addWrappedText, addFooters, savePDF

// ── Helper: resolve variable references ────────────────────────────────────
function rv(text, variables) {
  return replaceVariables(text || '', variables);
}

// formatDate and hexToRgb imported from pdfShared.js

// ── 1. Cover Page ──────────────────────────────────────────────────────────
async function renderCoverPage(doc, proposalData) {
  const vars = proposalData.variables || {};
  const cover = proposalData.cover || {};

  // Full-page navy background
  doc.setFillColor(...NAVY);
  doc.rect(0, 0, PAGE.width, PAGE.height, 'F');

  // Try to load and draw background image
  const bgUrl = cover.background_image_type === 'local' && cover.background_image_file
    ? cover.background_image_file
    : cover.background_image_url;

  if (bgUrl) {
    const imgData = await loadImageAsBase64(bgUrl);
    if (imgData) {
      try {
        doc.addImage(imgData, 'JPEG', 0, 0, PAGE.width, PAGE.height);
        // Overlay with semi-transparent navy
        const opacity = (cover.overlay_opacity ?? 60) / 100;
        doc.saveGraphicsState();
        doc.setGState(doc.GState({ opacity }));
        doc.setFillColor(...NAVY);
        doc.rect(0, 0, PAGE.width, PAGE.height, 'F');
        doc.restoreGraphicsState();
      } catch (e) {
        console.warn('Cover background image failed:', e);
      }
    }
  }

  // Gold accent line at top
  doc.setFillColor(...GOLD);
  doc.rect(PAGE.margin, 40, PAGE.width - 2 * PAGE.margin, 1.2, 'F');

  // Title — centered
  const titleText = rv(cover.title_template || '{{project_name}} / PROPOSAL', vars);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(32);
  doc.setTextColor(...WHITE);
  const titleLines = doc.splitTextToSize(titleText, CONTENT_WIDTH);
  let titleY = 110;
  for (const line of titleLines) {
    doc.text(line, PAGE.width / 2, titleY, { align: 'center' });
    titleY += 14;
  }

  // Gold divider below title
  doc.setFillColor(...GOLD);
  const dividerY = titleY + 8;
  doc.rect(PAGE.width / 2 - 30, dividerY, 60, 1, 'F');

  // Revision + Date — top right
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(200, 200, 200);
  const revText = vars.proposal_revision ? `Revision ${vars.proposal_revision}` : '';
  const dateText = formatDate(vars.proposal_date);
  if (revText) doc.text(revText, PAGE.width - PAGE.margin, 50, { align: 'right' });
  if (dateText) doc.text(dateText, PAGE.width - PAGE.margin, 56, { align: 'right' });

  // Bottom two-column info
  const colY = PAGE.height - 80;
  const colWidth = CONTENT_WIDTH / 2 - 4;
  const leftX = PAGE.margin;
  const rightX = PAGE.margin + CONTENT_WIDTH / 2 + 4;

  // Left column — PRESENTED TO
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(8);
  doc.setTextColor(...GOLD);
  doc.text('PRESENTED TO', leftX, colY);

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  doc.setTextColor(...WHITE);
  doc.text(vars.client_name || '', leftX, colY + 7);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  doc.setTextColor(200, 200, 200);
  let ly = colY + 14;
  if (vars.client_contact_person) { doc.text(vars.client_contact_person, leftX, ly); ly += 5; }
  if (vars.client_address) {
    const addrLines = doc.splitTextToSize(vars.client_address, colWidth);
    for (const line of addrLines) { doc.text(line, leftX, ly); ly += 5; }
  }
  if (vars.client_phone) { doc.text(`Phone: ${vars.client_phone}`, leftX, ly); ly += 5; }
  if (vars.client_email) { doc.text(vars.client_email, leftX, ly); ly += 5; }

  // Right column — PRESENTED BY
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(8);
  doc.setTextColor(...GOLD);
  doc.text('PRESENTED BY', rightX, colY);

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  doc.setTextColor(...WHITE);
  doc.text(vars.company_name || getBRAND_NAME(), rightX, colY + 7);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  doc.setTextColor(200, 200, 200);
  let ry = colY + 14;
  if (vars.company_acn) { doc.text(`ACN: ${vars.company_acn}`, rightX, ry); ry += 5; }
  if (vars.company_address) {
    const addrLines = doc.splitTextToSize(vars.company_address, colWidth);
    for (const line of addrLines) { doc.text(line, rightX, ry); ry += 5; }
  }
  if (vars.company_phone) { doc.text(`Phone: ${vars.company_phone}`, rightX, ry); ry += 5; }
  if (vars.company_website) { doc.text(`Web: ${vars.company_website}`, rightX, ry); ry += 5; }
  if (vars.company_director) { doc.text(`${vars.company_director}, ${vars.company_director_title || 'Director'}`, rightX, ry); ry += 5; }

  // Partner logos at bottom
  const logos = proposalData.partner_logos || [];
  const coverLogos = logos.filter(l => l.display_position === 'cover' || l.display_position === 'all');
  if (coverLogos.length > 0) {
    const logoY = PAGE.height - 35;
    const logoH = 8;
    const logoW = 16;
    const totalW = coverLogos.length * (logoW + 4) - 4;
    let logoX = (PAGE.width - totalW) / 2;
    for (const logo of coverLogos) {
      const logoUrl = logo.source_type === 'local' && logo.file ? logo.file : logo.image_url;
      if (logoUrl) {
        const imgData = await loadImageAsBase64(logoUrl);
        if (imgData) {
          try {
            doc.addImage(imgData, 'PNG', logoX, logoY, logoW, logoH);
          } catch (_) {}
        }
      }
      logoX += logoW + 4;
    }
  }
}

// ── 2. Cover Letter ────────────────────────────────────────────────────────
function renderCoverLetter(doc, proposalData, y) {
  const vars = proposalData.variables || {};
  const cl = proposalData.cover_letter || {};

  // Date — right aligned
  const dateText = formatDate(vars.proposal_date);
  if (dateText) {
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.setTextColor(...TEXT_COLOR);
    doc.text(dateText, PAGE.width - PAGE.margin, y, { align: 'right' });
    y += 12;
  }

  // Recipient block
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  doc.setTextColor(...TEXT_COLOR);
  if (vars.client_contact_person) {
    doc.text(vars.client_contact_person, PAGE.margin, y); y += 5;
  }
  if (vars.client_contact_title) {
    doc.text(vars.client_contact_title, PAGE.margin, y); y += 5;
  }
  if (vars.client_name) {
    doc.setFont('helvetica', 'bold');
    doc.text(vars.client_name, PAGE.margin, y); y += 5;
    doc.setFont('helvetica', 'normal');
  }
  if (vars.client_address) {
    const addrLines = doc.splitTextToSize(vars.client_address, CONTENT_WIDTH);
    for (const line of addrLines) {
      doc.text(line, PAGE.margin, y); y += 5;
    }
  }
  y += 6;

  // Salutation
  const dearName = vars.client_contact_person || vars.client_name || 'Sir/Madam';
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  doc.setTextColor(...TEXT_COLOR);
  doc.text(`Dear ${dearName},`, PAGE.margin, y);
  y += 10;

  // Body text with variable replacement
  const bodyText = rv(cl.body || '', vars);
  const paragraphs = bodyText.split('\n\n');
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  doc.setTextColor(...TEXT_COLOR);

  for (const para of paragraphs) {
    if (para.trim() === '') continue;
    y = addWrappedText(doc, para.trim(), PAGE.margin, y, CONTENT_WIDTH, 5.5);
    y += 4; // paragraph spacing
  }
  y += 6;

  // Closing
  doc.text('Yours sincerely,', PAGE.margin, y); y += 12;

  // Signature name
  doc.setFont('helvetica', 'bold');
  doc.text(vars.company_director || '', PAGE.margin, y); y += 5;
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(...MID_GRAY);
  doc.text(vars.company_director_title || 'Director', PAGE.margin, y); y += 5;
  doc.setTextColor(...TEXT_COLOR);
  doc.setFontSize(10);
  doc.text(vars.company_name || getBRAND_NAME(), PAGE.margin, y);

  return y;
}

// ── 3. Competitive Advantages ──────────────────────────────────────────────
function renderAdvantages(doc, proposalData, y) {
  const vars = proposalData.variables || {};
  const advantages = proposalData.advantages || [];

  // Section heading
  y = ensureSpace(doc, y, 20);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(14);
  doc.setTextColor(...NAVY);
  doc.text('COMPETITIVE ADVANTAGE', PAGE.margin, y);
  y += 4;
  doc.setDrawColor(...GOLD);
  doc.setLineWidth(0.6);
  doc.line(PAGE.margin, y, PAGE.margin + 50, y);
  y += 10;

  // 2-column grid layout
  const colWidth = (CONTENT_WIDTH - 10) / 2;
  const cardHeight = 50;
  const gapX = 10;
  const gapY = 8;

  for (let i = 0; i < advantages.length; i++) {
    const col = i % 2;
    const row = Math.floor(i / 2);

    // Check if we need a new page
    if (col === 0) {
      y = ensureSpace(doc, y, cardHeight + gapY);
    }

    const x = PAGE.margin + col * (colWidth + gapX);
    const cardY = y;

    // Card background
    doc.setFillColor(...LIGHT_GRAY);
    doc.roundedRect(x, cardY, colWidth, cardHeight, 3, 3, 'F');

    // Gold circle with number
    const circleR = 8;
    const circleX = x + 12;
    const circleY = cardY + 14;
    doc.setFillColor(...GOLD);
    doc.circle(circleX, circleY, circleR, 'F');

    // Number text
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(12);
    doc.setTextColor(...WHITE);
    doc.text(String(i + 1), circleX, circleY + 1, { align: 'center' });

    // Title
    const titleX = x + 26;
    const titleMaxW = colWidth - 32;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.setTextColor(...NAVY);
    doc.text(advantages[i].title || '', titleX, cardY + 12, { maxWidth: titleMaxW });

    // Description
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.setTextColor(...MID_GRAY);
    const descLines = doc.splitTextToSize(advantages[i].description || '', titleMaxW);
    let dy = cardY + 20;
    for (const line of descLines.slice(0, 5)) {
      doc.text(line, titleX, dy);
      dy += 4;
    }

    // If right column card, advance y
    if (col === 1 || i === advantages.length - 1) {
      y = cardY + cardHeight + gapY;
    }
  }

  return y;
}

// ── 4. Selected Projects Intro (transition page) ──────────────────────────
function renderSelectedProjectsIntro(doc, proposalData) {
  const vars = proposalData.variables || {};
  const intro = proposalData.selected_projects_intro || {};

  // Full navy background
  doc.setFillColor(...NAVY);
  doc.rect(0, 0, PAGE.width, PAGE.height, 'F');

  // Gold accent line at top
  doc.setFillColor(...GOLD);
  doc.rect(PAGE.margin, 40, PAGE.width - 2 * PAGE.margin, 1.2, 'F');

  // Centered title
  const title = rv(intro.title || 'SELECTED PROJECTS', vars);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(36);
  doc.setTextColor(...GOLD);
  doc.text(title, PAGE.width / 2, 130, { align: 'center' });

  // Gold divider
  doc.setFillColor(...GOLD);
  doc.rect(PAGE.width / 2 - 30, 142, 60, 1, 'F');

  // Subtitle
  if (intro.subtitle) {
    const subtitle = rv(intro.subtitle, vars);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(11);
    doc.setTextColor(200, 200, 200);
    const lines = doc.splitTextToSize(subtitle, CONTENT_WIDTH - 40);
    let sy = 160;
    for (const line of lines) {
      doc.text(line, PAGE.width / 2, sy, { align: 'center' });
      sy += 6;
    }
  }

  // Bottom gold line
  doc.setFillColor(...GOLD);
  doc.rect(PAGE.margin, PAGE.height - 40, PAGE.width - 2 * PAGE.margin, 1.2, 'F');
}

// ── 5. Project Cases ──────────────────────────────────────────────────────
async function renderProjectCases(doc, proposalData, y) {
  const vars = proposalData.variables || {};
  const cases = proposalData.project_cases || [];

  // Section heading
  y = ensureSpace(doc, y, 20);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(14);
  doc.setTextColor(...NAVY);
  doc.text('PROJECT CASES', PAGE.margin, y);
  y += 4;
  doc.setDrawColor(...GOLD);
  doc.setLineWidth(0.6);
  doc.line(PAGE.margin, y, PAGE.margin + 50, y);
  y += 10;

  // Helper: load image as base64 with natural pixel dimensions for adaptive aspect ratio
  const loadImgDim = async (url) => {
    const data = await loadImageAsBase64(url);
    if (!data) return null;
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = () => resolve({ data, w: img.naturalWidth, h: img.naturalHeight });
      img.onerror = () => resolve({ data, w: 4, h: 3 });
      img.src = data;
    });
  };

  const COL_GAP = 8;                              // mm between image columns
  const IMG_W = (CONTENT_WIDTH - COL_GAP) / 2;   // ~86mm per column
  const ROW_GAP = 6;                              // mm between image rows
  const TEXT_IMG_GAP = 6;                         // mm between text block and image grid

  for (let i = 0; i < cases.length; i++) {
    const pc = cases[i];

    // Load all images upfront with dimensions
    const rawImages = pc.images || [];
    const loadedImages = [];
    for (const img of rawImages) {
      const imgUrl = img.source_type === 'local' && img.file ? img.file : img.url;
      loadedImages.push(imgUrl ? await loadImgDim(imgUrl) : null);
    }
    if (loadedImages.length === 0) loadedImages.push(null); // ensure at least one placeholder

    // Estimate text block height
    let textH = 8; // project name
    if (pc.client_name) textH += 6;
    if (pc.description) {
      textH += doc.splitTextToSize(pc.description, CONTENT_WIDTH).length * 4.5;
    }
    if (pc.highlights && pc.highlights.length > 0) {
      textH += 7 + pc.highlights.length * 4.5;
    }

    // Group images into rows of 2 and calculate adaptive row heights
    const rows = [];
    for (let r = 0; r < loadedImages.length; r += 2) rows.push(loadedImages.slice(r, r + 2));
    const rowHeights = rows.map(row =>
      Math.max(...row.map(img => img ? IMG_W * (img.h / img.w) : IMG_W * 0.65))
    );
    const imgGridH = rowHeights.reduce((sum, h) => sum + h + ROW_GAP, 0);

    // Page break if content won't fit
    if (y + textH + TEXT_IMG_GAP + imgGridH > PAGE.height - PAGE.margin) {
      doc.addPage();
      y = PAGE.margin;
    }

    let ty = y;

    // ── Text block (full width) ──
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(12);
    doc.setTextColor(...NAVY);
    doc.text(pc.project_name || 'Untitled Project', PAGE.margin, ty, { maxWidth: CONTENT_WIDTH });
    ty += 8;

    if (pc.client_name) {
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(9);
      doc.setTextColor(...GOLD);
      doc.text(`Client: ${pc.client_name}`, PAGE.margin, ty, { maxWidth: CONTENT_WIDTH });
      ty += 6;
    }

    if (pc.description) {
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(8);
      doc.setTextColor(...TEXT_COLOR);
      const descLines = doc.splitTextToSize(pc.description, CONTENT_WIDTH);
      for (const line of descLines) {
        doc.text(line, PAGE.margin, ty);
        ty += 4.5;
      }
    }

    if (pc.highlights && pc.highlights.length > 0) {
      ty += 2;
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(8);
      doc.setTextColor(...GOLD);
      doc.text('Key Highlights:', PAGE.margin, ty);
      ty += 5;
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(...TEXT_COLOR);
      for (const hl of pc.highlights) {
        const hlText = typeof hl === 'object' ? hl.text || hl : hl;
        doc.text(`• ${hlText}`, PAGE.margin + 2, ty, { maxWidth: CONTENT_WIDTH - 4 });
        ty += 4.5;
      }
    }

    ty += TEXT_IMG_GAP;

    // ── Image grid (2-column, adaptive aspect ratio) ──
    for (let ri = 0; ri < rows.length; ri++) {
      const row = rows[ri];
      const rowH = rowHeights[ri];
      for (let ci = 0; ci < row.length; ci++) {
        const imgInfo = row[ci];
        const imgX = PAGE.margin + ci * (IMG_W + COL_GAP);
        const imgH = imgInfo ? IMG_W * (imgInfo.h / imgInfo.w) : IMG_W * 0.65;
        const imgY = ty + (rowH - imgH) / 2; // vertically center shorter image in row

        if (imgInfo && imgInfo.data) {
          try {
            doc.addImage(imgInfo.data, 'JPEG', imgX, imgY, IMG_W, imgH);
          } catch (_) {
            doc.setFillColor(220, 220, 220);
            doc.roundedRect(imgX, imgY, IMG_W, imgH, 2, 2, 'F');
          }
        } else {
          doc.setFillColor(220, 220, 220);
          doc.roundedRect(imgX, imgY, IMG_W, imgH, 2, 2, 'F');
          doc.setFont('helvetica', 'normal');
          doc.setFontSize(8);
          doc.setTextColor(...MID_GRAY);
          doc.text('No Image', imgX + IMG_W / 2, imgY + imgH / 2, { align: 'center' });
        }
      }
      ty += rowH + ROW_GAP;
    }

    y = ty + 12; // gap between cases
  }

  return y;
}

// ── 6. Project Alignment ──────────────────────────────────────────────────
function renderProjectAlignment(doc, proposalData, y) {
  const vars = proposalData.variables || {};
  const pa = proposalData.project_alignment || {};

  // Section heading
  y = ensureSpace(doc, y, 20);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(14);
  doc.setTextColor(...NAVY);
  doc.text('PROJECT ALIGNMENT', PAGE.margin, y);
  y += 4;
  doc.setDrawColor(...GOLD);
  doc.setLineWidth(0.6);
  doc.line(PAGE.margin, y, PAGE.margin + 50, y);
  y += 8;

  // Intro
  if (pa.intro) {
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.setTextColor(...TEXT_COLOR);
    y = addWrappedText(doc, rv(pa.intro, vars), PAGE.margin, y, CONTENT_WIDTH, 5.5);
    y += 6;
  }

  // Two-column layout: Requirements vs Capabilities
  const points = pa.points || [];
  if (points.length > 0) {
    const colWidth = (CONTENT_WIDTH - 10) / 2;
    const leftX = PAGE.margin;
    const rightX = PAGE.margin + colWidth + 10;

    // Column headers
    y = ensureSpace(doc, y, 12);
    doc.setFillColor(...NAVY);
    doc.roundedRect(leftX, y, colWidth, 8, 2, 2, 'F');
    doc.roundedRect(rightX, y, colWidth, 8, 2, 2, 'F');

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    doc.setTextColor(...WHITE);
    doc.text('Requirements', leftX + 4, y + 5.5);
    doc.text('Our Capability', rightX + 4, y + 5.5);
    y += 12;

    for (const point of points) {
      y = ensureSpace(doc, y, 14);
      const rowH = 12;

      // Alternating background
      const bgColor = points.indexOf(point) % 2 === 0 ? LIGHT_GRAY : WHITE;
      doc.setFillColor(...bgColor);
      doc.rect(leftX, y - 2, colWidth, rowH, 'F');
      doc.rect(rightX, y - 2, colWidth, rowH, 'F');

      // Gold bullet + requirement
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(8);
      doc.setTextColor(...GOLD);
      doc.text('●', leftX + 4, y + 3);
      doc.setTextColor(...TEXT_COLOR);
      const reqLines = doc.splitTextToSize(point.requirement || '', colWidth - 12);
      doc.text(reqLines[0] || '', leftX + 10, y + 3);

      // Checkmark + capability
      doc.setTextColor(22, 163, 74); // green check
      doc.setFontSize(9);
      doc.text('✓', rightX + 4, y + 3);
      doc.setTextColor(...TEXT_COLOR);
      doc.setFontSize(8);
      const capLines = doc.splitTextToSize(point.our_capability || '', colWidth - 12);
      doc.text(capLines[0] || '', rightX + 10, y + 3);

      y += rowH;
    }
    y += 4;
  }

  // Summary
  if (pa.summary) {
    y = ensureSpace(doc, y, 20);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.setTextColor(...NAVY);
    doc.text('Summary', PAGE.margin, y);
    y += 6;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.setTextColor(...TEXT_COLOR);
    y = addWrappedText(doc, rv(pa.summary, vars), PAGE.margin, y, CONTENT_WIDTH, 5);
    y += 6;
  }

  return y;
}

// ── 7. Delivery Approach ──────────────────────────────────────────────────
function renderDeliveryApproach(doc, proposalData, y) {
  const vars = proposalData.variables || {};
  const da = proposalData.delivery_approach || {};
  const steps = da.steps || [];

  // Section heading
  y = ensureSpace(doc, y, 20);
  const heading = rv(da.title || 'DELIVERY APPROACH', vars);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(14);
  doc.setTextColor(...NAVY);
  doc.text(heading, PAGE.margin, y);
  y += 4;
  doc.setDrawColor(...GOLD);
  doc.setLineWidth(0.6);
  doc.line(PAGE.margin, y, PAGE.margin + 50, y);
  y += 12;

  const stepHeight = 30;
  const circleR = 10;
  const circleX = PAGE.margin + circleR + 2;

  for (let i = 0; i < steps.length; i++) {
    y = ensureSpace(doc, y, stepHeight + 8);
    if (y + stepHeight + 8 > PAGE.height - PAGE.margin - 15) {
      doc.addPage();
      y = PAGE.margin;
    }

    const stepY = y;

    // Connection line (from previous step)
    if (i > 0) {
      doc.setDrawColor(...GOLD);
      doc.setLineWidth(0.5);
      doc.setLineDashPattern([2, 2], 0);
      doc.line(circleX, stepY - stepHeight + circleR + 10, circleX, stepY - 2);
      doc.setLineDashPattern([], 0);
    }

    // Gold circle with step number
    doc.setFillColor(...GOLD);
    doc.circle(circleX, stepY + circleR, circleR, 'F');

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(12);
    doc.setTextColor(...WHITE);
    doc.text(String(steps[i].step_number || i + 1), circleX, stepY + circleR + 1.5, { align: 'center' });

    // Step title
    const textX = circleX + circleR + 10;
    const textMaxW = CONTENT_WIDTH - circleR * 2 - 14;

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.setTextColor(...NAVY);
    doc.text(steps[i].title || '', textX, stepY + circleR - 2);

    // Step description
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.setTextColor(...TEXT_COLOR);
    const descLines = doc.splitTextToSize(steps[i].description || '', textMaxW);
    let dy = stepY + circleR + 5;
    for (const line of descLines.slice(0, 3)) {
      doc.text(line, textX, dy);
      dy += 4.5;
    }

    y = Math.max(stepY + stepHeight, dy + 4);
  }

  return y;
}

// ── 8. Work Schedule (Calendar) ───────────────────────────────────────────
function renderWorkSchedule(doc, proposalData, y) {
  const vars = proposalData.variables || {};
  const ws = proposalData.work_schedule || {};

  // Section heading
  y = ensureSpace(doc, y, 20);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(14);
  doc.setTextColor(...NAVY);
  doc.text('WORK SCHEDULE', PAGE.margin, y);
  y += 4;
  doc.setDrawColor(...GOLD);
  doc.setLineWidth(0.6);
  doc.line(PAGE.margin, y, PAGE.margin + 50, y);
  y += 10;

  const startDate = ws.start_date ? new Date(ws.start_date + 'T00:00:00') : null;
  const endDate = ws.end_date ? new Date(ws.end_date + 'T00:00:00') : null;
  const milestones = ws.milestones || [];

  // Determine months to display
  const months = [];
  if (startDate && endDate) {
    const d = new Date(startDate.getFullYear(), startDate.getMonth(), 1);
    while (d <= endDate) {
      months.push({ year: d.getFullYear(), month: d.getMonth() });
      d.setMonth(d.getMonth() + 1);
    }
  } else if (milestones.length > 0) {
    // Derive from milestones
    const dates = milestones
      .filter(m => m.date)
      .map(m => new Date(m.date + 'T00:00:00'))
      .sort((a, b) => a - b);
    if (dates.length > 0) {
      const d = new Date(dates[0].getFullYear(), dates[0].getMonth(), 1);
      const last = dates[dates.length - 1];
      while (d <= last) {
        months.push({ year: d.getFullYear(), month: d.getMonth() });
        d.setMonth(d.getMonth() + 1);
      }
    }
  }

  if (months.length === 0) {
    // No date info, just show milestones list
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.setTextColor(...MID_GRAY);
    doc.text('No schedule dates configured. Add start/end dates or milestones.', PAGE.margin, y);
    return y + 6;
  }

  const monthNames = ['January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'];
  const dayNames = ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'];

  // Milestone date lookup
  const milestoneDates = {};
  for (const m of milestones) {
    if (m.date) {
      const key = m.date;
      if (!milestoneDates[key]) milestoneDates[key] = [];
      milestoneDates[key].push(m);
    }
  }

  for (const { year, month } of months) {
    // Each month needs about 50mm
    y = ensureSpace(doc, y, 55);
    if (y + 55 > PAGE.height - PAGE.margin - 15) {
      doc.addPage();
      y = PAGE.margin;
    }

    // Month header
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(12);
    doc.setTextColor(...NAVY);
    doc.text(`${monthNames[month]} ${year}`, PAGE.margin, y);
    y += 7;

    // Calendar grid
    const cellW = (CONTENT_WIDTH) / 7;
    const cellH = 7;
    const gridX = PAGE.margin;

    // Day name headers
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(7);
    doc.setTextColor(...MID_GRAY);
    for (let d = 0; d < 7; d++) {
      doc.text(dayNames[d], gridX + d * cellW + cellW / 2, y, { align: 'center' });
    }
    y += 4;

    // Draw calendar days
    const firstDay = new Date(year, month, 1);
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    // getDay: 0=Sun, need Mo=0
    let startDow = firstDay.getDay() - 1;
    if (startDow < 0) startDow = 6;

    let dayNum = 1;
    // Pre-compute iteration dates once to avoid new Date() in inner loop
    const iterDate = new Date(year, month, 1);
    for (let row = 0; row < 6; row++) {
      if (dayNum > daysInMonth) break;
      for (let col = 0; col < 7; col++) {
        const cx = gridX + col * cellW;
        if (row === 0 && col < startDow) continue;
        if (dayNum > daysInMonth) break;

        const isMilestone = milestoneDates[`${year}-${String(month + 1).padStart(2, '0')}-${String(dayNum).padStart(2, '0')}`];
        const isInRange = startDate && endDate &&
          iterDate >= startDate && iterDate <= endDate;

        // Cell background
        if (isMilestone) {
          doc.setFillColor(...GOLD);
          doc.roundedRect(cx + 1, y - 1, cellW - 2, cellH - 1, 1, 1, 'F');
          doc.setFont('helvetica', 'bold');
          doc.setFontSize(7);
          doc.setTextColor(...WHITE);
        } else if (isInRange) {
          doc.setFillColor(...LIGHT_GRAY);
          doc.rect(cx + 1, y - 1, cellW - 2, cellH - 1, 'F');
          doc.setFont('helvetica', 'normal');
          doc.setFontSize(7);
          doc.setTextColor(...TEXT_COLOR);
        } else {
          doc.setFont('helvetica', 'normal');
          doc.setFontSize(7);
          doc.setTextColor(...MID_GRAY);
        }

        doc.text(String(dayNum), cx + cellW / 2, y + 3, { align: 'center' });
        dayNum++;
        iterDate.setDate(iterDate.getDate() + 1);
      }
      y += cellH;
    }
    y += 6;
  }

  // Milestones list
  if (milestones.length > 0) {
    y = ensureSpace(doc, y, 10 + milestones.length * 8);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.setTextColor(...NAVY);
    doc.text('Milestones', PAGE.margin, y);
    y += 6;

    for (const m of milestones) {
      y = ensureSpace(doc, y, 10);
      const mDate = m.date ? formatDate(m.date) : 'TBD';
      // Color dot
      const dotColor = m.color ? hexToRgb(m.color) : GOLD;
      doc.setFillColor(...dotColor);
      doc.circle(PAGE.margin + 3, y - 1, 2, 'F');

      doc.setFont('helvetica', 'bold');
      doc.setFontSize(9);
      doc.setTextColor(...NAVY);
      doc.text(m.name || 'Milestone', PAGE.margin + 9, y);

      doc.setFont('helvetica', 'normal');
      doc.setFontSize(8);
      doc.setTextColor(...MID_GRAY);
      doc.text(mDate, PAGE.margin + 9 + doc.getTextWidth(m.name || 'Milestone') + 8, y);

      if (m.description) {
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(8);
        doc.setTextColor(...TEXT_COLOR);
        const descLines = doc.splitTextToSize(m.description, CONTENT_WIDTH - 20);
        y += 4;
        for (const line of descLines.slice(0, 2)) {
          doc.text(line, PAGE.margin + 9, y);
          y += 4;
        }
      }
      y += 4;
    }
  }

  return y;
}

// ── 9. Resources & Summary ────────────────────────────────────────────────
function renderResourcesSummary(doc, proposalData, y) {
  const vars = proposalData.variables || {};
  const rs = proposalData.resources_summary || {};

  // Section heading
  y = ensureSpace(doc, y, 20);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(14);
  doc.setTextColor(...NAVY);
  doc.text('RESOURCES & SUMMARY', PAGE.margin, y);
  y += 4;
  doc.setDrawColor(...GOLD);
  doc.setLineWidth(0.6);
  doc.line(PAGE.margin, y, PAGE.margin + 50, y);
  y += 10;

  // Team section
  const team = rs.team || [];
  if (team.length > 0) {
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.setTextColor(...NAVY);
    doc.text('Our Team', PAGE.margin, y);
    y += 8;

    // 3-column card grid
    const cardW = (CONTENT_WIDTH - 8) / 3;
    const cardH = 35;

    for (let i = 0; i < team.length; i++) {
      const col = i % 3;
      const row = Math.floor(i / 3);

      if (col === 0) {
        y = ensureSpace(doc, y, cardH + 6);
      }

      const cx = PAGE.margin + col * (cardW + 4);

      // Card background
      doc.setFillColor(...LIGHT_GRAY);
      doc.roundedRect(cx, y, cardW, cardH, 3, 3, 'F');

      // Avatar circle
      const avatarR = 6;
      const avatarX = cx + cardW / 2;
      const avatarY = y + 10;
      doc.setFillColor(...GOLD);
      doc.circle(avatarX, avatarY, avatarR, 'F');

      // Initials
      const name = team[i].name || 'N/A';
      const initials = name.split(' ').map(w => w[0]).join('').substring(0, 2).toUpperCase();
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(8);
      doc.setTextColor(...WHITE);
      doc.text(initials, avatarX, avatarY + 1, { align: 'center' });

      // Name
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(8);
      doc.setTextColor(...NAVY);
      doc.text(name, cx + cardW / 2, y + 21, { align: 'center', maxWidth: cardW - 6 });

      // Role
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(7);
      doc.setTextColor(...GOLD);
      doc.text(team[i].role || '', cx + cardW / 2, y + 26, { align: 'center', maxWidth: cardW - 6 });

      // Description (1 line)
      if (team[i].description) {
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(6.5);
        doc.setTextColor(...MID_GRAY);
        const descLine = doc.splitTextToSize(team[i].description, cardW - 8)[0];
        doc.text(descLine, cx + cardW / 2, y + 31, { align: 'center' });
      }

      if (col === 2 || i === team.length - 1) {
        y += cardH + 6;
      }
    }
  }

  // Key metrics
  const metrics = rs.key_metrics || [];
  if (metrics.length > 0) {
    y = ensureSpace(doc, y, 30);
    const metricW = CONTENT_WIDTH / metrics.length;
    const metricY = y;

    doc.setFillColor(...NAVY);
    doc.roundedRect(PAGE.margin, metricY, CONTENT_WIDTH, 25, 3, 3, 'F');

    for (let i = 0; i < metrics.length; i++) {
      const mx = PAGE.margin + i * metricW + metricW / 2;

      // Value in gold
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(14);
      doc.setTextColor(...GOLD);
      doc.text(metrics[i].value || '', mx, metricY + 10, { align: 'center' });

      // Label
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(7);
      doc.setTextColor(200, 200, 200);
      doc.text(metrics[i].label || '', mx, metricY + 17, { align: 'center' });
    }
    y += 32;
  }

  // Summary
  if (rs.summary) {
    y = ensureSpace(doc, y, 20);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.setTextColor(...NAVY);
    doc.text('Summary', PAGE.margin, y);
    y += 6;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.setTextColor(...TEXT_COLOR);
    y = addWrappedText(doc, rv(rs.summary, vars), PAGE.margin, y, CONTENT_WIDTH, 5);
    y += 6;
  }

  return y;
}

// hexToRgb imported from pdfShared.js

// ── Internal: build proposal pages into an existing doc ─────────────────────
async function buildProposalIntoDoc(doc, proposalData) {
  doc.setTextColor(...TEXT_COLOR);

  // 1. Cover page — always its own page (full visual design)
  await renderCoverPage(doc, proposalData);
  doc.addPage();

  // 2–9. Flow layout: sections render continuously, only adding pages
  // when remaining space is insufficient (via ensureSpace inside each renderer).
  // A small gap between sections provides visual separation instead of forced page breaks.
  let y = PAGE.margin;

  // 2. Cover letter
  y = renderCoverLetter(doc, proposalData, y);
  y += 10; // section gap

  // 3. Competitive advantages — always starts on a new page
  doc.addPage();
  y = PAGE.margin;
  y = renderAdvantages(doc, proposalData, y);
  y += 10;

  // 4. Selected projects intro — full-page navy visual, must be its own page
  doc.addPage();
  renderSelectedProjectsIntro(doc, proposalData);
  doc.addPage();
  y = PAGE.margin;

  // 5. Project cases
  y = ensureSpace(doc, y, 20);
  y = await renderProjectCases(doc, proposalData, y);
  y += 10;

  // 6. Project alignment
  y = ensureSpace(doc, y, 20);
  y = renderProjectAlignment(doc, proposalData, y);
  y += 10;

  // 7. Delivery approach
  y = ensureSpace(doc, y, 20);
  y = renderDeliveryApproach(doc, proposalData, y);
  y += 10;

  // 8. Work schedule
  y = ensureSpace(doc, y, 20);
  y = renderWorkSchedule(doc, proposalData, y);
  y += 10;

  // 9. Resources & summary
  y = ensureSpace(doc, y, 20);
  renderResourcesSummary(doc, proposalData, y);

  return doc;
}

// ── Main Export ────────────────────────────────────────────────────────────
export async function generateProposalPDF(proposalData, mode = 'download') {
  const doc = new jsPDF('p', 'mm', 'a4');
  await buildProposalIntoDoc(doc, proposalData);
  addFooters(doc, proposalData.variables?.company_name || getBRAND_NAME());

  const clientName = (proposalData.variables?.client_name || 'Client').replace(/\s+/g, '_');
  const fileName = `Proposal_${proposalData.proposal_no || 'DRAFT'}_${clientName}_${proposalData.variables?.proposal_date || 'undated'}.pdf`;

  if (mode === 'preview') {
    const blob = doc.output('blob');
    window.open(URL.createObjectURL(blob), '_blank');
  } else {
    await savePDF(doc, fileName);
  }
}

// ── Merged: Proposal + Quote in one PDF ──────────────────────────────────────
/**
 * Generate a combined PDF: all proposal pages followed by all quote pages.
 * @param {object} proposalData  — full proposal data object
 * @param {object} quoteData     — full quote data object (from API or localStorage)
 */
export async function generateMergedPDF(proposalData, quoteData) {
  const doc = new jsPDF('p', 'mm', 'a4');

  // ── Part 1: Proposal pages ──────────────────────────────────────────────
  await buildProposalIntoDoc(doc, proposalData);

  // ── Divider page between proposal and quote ─────────────────────────────
  doc.addPage();
  doc.setFillColor(...NAVY);
  doc.rect(0, 0, PAGE.width, PAGE.height, 'F');
  doc.setFillColor(...GOLD);
  doc.rect(PAGE.margin, 40, PAGE.width - 2 * PAGE.margin, 1.2, 'F');
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(28);
  doc.setTextColor(...GOLD);
  doc.text('QUOTATION', PAGE.width / 2, PAGE.height / 2 - 10, { align: 'center' });
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(12);
  doc.setTextColor(200, 200, 200);
  doc.text(
    quoteData.quote_no || '',
    PAGE.width / 2, PAGE.height / 2 + 4, { align: 'center' }
  );
  doc.setFillColor(...GOLD);
  doc.rect(PAGE.margin, PAGE.height - 40, PAGE.width - 2 * PAGE.margin, 1.2, 'F');

  // ── Part 2: Quote pages ─────────────────────────────────────────────────
  const { subtotal, gstAmount, total, paymentTotal, retentionPercentage } =
    computeQuoteFinancials(quoteData);

  await buildQuoteIntoDoc(doc, quoteData, subtotal, gstAmount, total, paymentTotal, retentionPercentage);

  // ── Footers across all pages ────────────────────────────────────────────
  const companyName = proposalData.variables?.company_name || getBRAND_NAME();
  addFooters(doc, companyName);

  // ── Save ────────────────────────────────────────────────────────────────
  const clientName = (proposalData.variables?.client_name || 'Client').replace(/\s+/g, '_');
  const fileName = `Proposal+Quote_${proposalData.proposal_no || 'DRAFT'}_${clientName}_${proposalData.variables?.proposal_date || 'undated'}.pdf`;
  await savePDF(doc, fileName);
}
